import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export const parse = (s) => { if (!s) return null; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));
const onDay = (s, day) => { const a = parse(s.start_date), b = parse(s.end_date) || a; return a && day >= a && day <= b; };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TRADE_HUE = (t = "") => { let h = 0; for (const c of t) h = (h * 31 + c.charCodeAt(0)) % 360; return t ? `hsl(${h} 45% 45%)` : "hsl(var(--primary))"; };

export function Nav({ label, onPrev, onNext, onToday }) {
  return (
    <div className="flex items-center gap-2" data-testid="schedule-nav">
      <Button variant="ghost" size="icon" onClick={onPrev} aria-label="Previous" data-testid="schedule-prev"><ChevronLeft className="h-4 w-4" /></Button>
      <p className="min-w-[180px] text-center font-serif text-lg" data-testid="schedule-range-label">{label}</p>
      <Button variant="ghost" size="icon" onClick={onNext} aria-label="Next" data-testid="schedule-next"><ChevronRight className="h-4 w-4" /></Button>
      <Button variant="outline" size="sm" className="rounded-full" onClick={onToday} data-testid="schedule-today">Today</Button>
    </div>
  );
}

function Chip({ s, onClick, names }) {
  return (
    <button type="button" onClick={onClick} title={s.title} className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] leading-tight text-white ${s.done ? "opacity-40 line-through" : ""}`}
      style={{ background: TRADE_HUE(s.trade) }} data-testid={`schedule-chip-${s.id}`}>{s.title}{(s.crew_ids || []).length > 0 && names ? ` · ${s.crew_ids.map((id) => (names[id] || "").split(" ")[0]).filter(Boolean).join(", ")}` : ""}</button>
  );
}

export function WeekView({ items, anchor, setAnchor, names, onPick }) {
  const start = mondayOf(anchor), days = Array.from({ length: 7 }, (_, i) => addDays(start, i)), today = iso(new Date());
  return (
    <div className="space-y-4" data-testid="schedule-week">
      <Nav label={`${iso(start)} → ${iso(addDays(start, 6))}`} onPrev={() => setAnchor(addDays(anchor, -7))} onNext={() => setAnchor(addDays(anchor, 7))} onToday={() => setAnchor(new Date())} />
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border bg-border">
        {days.map((d, i) => { const k = iso(d); const list = items.filter((s) => onDay(s, d)); return (
          <div key={k} className={`min-h-[160px] bg-card p-2 ${k === today ? "ring-2 ring-inset ring-accent" : ""}`} data-testid={`week-day-${k}`}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{DAYS[i]}</p><p className="font-mono text-sm">{d.getDate()}</p>
            <div className="mt-2 space-y-1">{list.map((s) => <Chip key={s.id} s={s} names={names} onClick={() => onPick(s)} />)}</div>
          </div>); })}
      </div>
    </div>
  );
}

export function MonthView({ items, anchor, setAnchor, names, onPick }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1), start = mondayOf(first), today = iso(new Date());
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const step = (n) => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + n, 1));
  return (
    <div className="space-y-4" data-testid="schedule-month">
      <Nav label={first.toLocaleDateString(undefined, { month: "long", year: "numeric" })} onPrev={() => step(-1)} onNext={() => step(1)} onToday={() => setAnchor(new Date())} />
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border bg-border text-[10px] uppercase tracking-wider text-muted-foreground">{DAYS.map((d) => <div key={d} className="bg-card px-2 py-1.5">{d}</div>)}
        {cells.map((d) => { const k = iso(d), out = d.getMonth() !== first.getMonth(); const list = items.filter((s) => onDay(s, d)); return (
          <div key={k} className={`min-h-[92px] bg-card p-1.5 normal-case tracking-normal ${out ? "opacity-40" : ""} ${k === today ? "ring-2 ring-inset ring-accent" : ""}`} data-testid={`month-day-${k}`}>
            <p className="font-mono text-xs text-foreground">{d.getDate()}</p>
            <div className="mt-1 space-y-0.5">{list.slice(0, 3).map((s) => <Chip key={s.id} s={s} names={names} onClick={() => onPick(s)} />)}{list.length > 3 && <p className="text-[10px] text-muted-foreground">+{list.length - 3} more</p>}</div>
          </div>); })}
      </div>
    </div>
  );
}

export function TimelineView({ items, onPick }) {
  const dated = items.filter((s) => s.start_date);
  if (dated.length === 0) return <p className="text-sm text-muted-foreground" data-testid="schedule-timeline-empty">Add start dates to see the timeline.</p>;
  const min = addDays(parse(dated.reduce((a, s) => (s.start_date < a ? s.start_date : a), dated[0].start_date)), -2);
  const maxIso = dated.reduce((a, s) => ((s.end_date || s.start_date) > a ? s.end_date || s.start_date : a), dated[0].start_date);
  const max = addDays(parse(maxIso), 3), span = Math.max(7, (max - min) / 86400000);
  const pct = (d) => `${Math.min(100, Math.max(0, ((d - min) / 86400000 / span) * 100))}%`;
  const todayX = pct(new Date());
  const months = []; for (let d = new Date(min); d <= max; d = addDays(d, 1)) if (d.getDate() === 1 || months.length === 0) months.push(new Date(d));
  return (
    <div className="overflow-x-auto rounded-xl border bg-card" data-testid="schedule-timeline">
      <div className="relative min-w-[640px] p-4">
        <div className="relative mb-2 ml-[180px] h-5 border-b text-[10px] uppercase tracking-wider text-muted-foreground">{months.map((m) => <span key={+m} className="absolute" style={{ left: pct(m) }}>{m.toLocaleDateString(undefined, { month: "short", day: m.getDate() === 1 ? undefined : "numeric" })}</span>)}</div>
        <div className="relative space-y-1.5">
          <span className="pointer-events-none absolute bottom-0 top-0 ml-[180px] w-px bg-accent" style={{ left: `calc(${todayX} - 0px)` }} data-testid="timeline-today" />
          {dated.map((s) => { const a = parse(s.start_date), b = addDays(parse(s.end_date) || a, 1); return (
            <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`timeline-row-${s.id}`}>
              <button type="button" onClick={() => onPick(s)} className={`w-[172px] shrink-0 truncate text-left ${s.done ? "text-muted-foreground line-through" : ""}`}>{s.title}</button>
              <div className="relative h-6 flex-1 rounded bg-secondary/60">
                <button type="button" onClick={() => onPick(s)} className={`absolute top-0 h-6 min-w-[6px] rounded text-left text-[10px] leading-6 text-white ${s.done ? "opacity-40" : ""}`} style={{ left: pct(a), width: `calc(${pct(b)} - ${pct(a)})`, background: TRADE_HUE(s.trade) }} title={`${s.start_date}${s.end_date ? ` → ${s.end_date}` : ""}`}>
                  <span className="truncate px-1.5">{s.trade || ""}</span>
                </button>
              </div>
            </div>); })}
        </div>
      </div>
    </div>
  );
}
